import { Category } from './types';
import { ALL_CATERING_AMENITIES } from './utils/catering.mjs';
import { Camera, PartyPopper, Presentation, Hammer, Trees, Sparkles } from 'lucide-react';

// Tailwind Styles - Blue & Rose Theme (Flat/No Shadow)
// Tailwind Styles - Premium "Midnight & Gold" Theme
export const CLS_BTN_PRIMARY = "btn-primary-gradient font-medium py-3 px-6 rounded-full active:scale-95 flex items-center justify-center gap-2 text-sm tracking-wide shadow-lg shadow-cyan-500/20 text-white";
export const CLS_BTN_SECONDARY = "bg-white dark:bg-stone-800 hover:bg-stone-50 dark:hover:bg-stone-700 text-stone-900 dark:text-white font-medium py-3 px-6 rounded-full border border-stone-900 dark:border-white transition-all active:scale-95 flex items-center justify-center gap-2 text-sm tracking-wide shadow-sm";
export const CLS_BTN_ICON = "p-3 rounded-full hover:bg-stone-100/80 dark:hover:bg-stone-800/80 transition-all text-stone-600 dark:text-stone-300 active:scale-90 backdrop-blur-sm";

export const CLS_INPUT = "w-full px-4 py-3.5 rounded-2xl border border-stone-200 dark:border-stone-700 bg-white/80 dark:bg-stone-800/50 backdrop-blur focus:border-stone-400 dark:focus:border-stone-500 focus:ring-4 focus:ring-stone-100 dark:focus:ring-stone-800 outline-none transition-all font-medium text-stone-900 dark:text-white placeholder:text-stone-400 dark:placeholder:text-stone-500 disabled:bg-stone-50 dark:disabled:bg-stone-900 disabled:text-stone-400 text-base shadow-sm";

// Card: Clean, soft shadow, no hard borders
export const CLS_CARD = "bg-white dark:bg-stone-900 rounded-3xl sm:rounded-2xl border border-stone-100 dark:border-stone-800 shadow-[0_2px_20px_rgb(0,0,0,0.04)] dark:shadow-none hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] hover:dark:shadow-[0_8px_30px_rgb(0,0,0,0.2)] hover:-translate-y-1 transition-all duration-300 group overflow-hidden";
export const TURNSTILE_SITE_KEY = (import.meta.env.VITE_TURNSTILE_SITE_KEY as string) || '1x00000000000000000000AA'; // Use VITE_TURNSTILE_SITE_KEY or fallback to test key

export const CLS_CONTAINER = "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8";
export const CLS_LABEL = "block text-sm font-bold text-stone-800 dark:text-stone-200 mb-2";
export const CLS_SECTION = "bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 p-4 sm:p-6 transition-colors shadow-sm";
export const CLS_SECTION_TITLE = "text-xl font-bold text-primary dark:text-white mb-1";
export const CLS_SECTION_SUBTITLE = "text-xs text-stone-500 dark:text-stone-400 mb-4 font-medium";
export const CLS_SECTION_INFO_BOX = "p-3 bg-stone-50 dark:bg-stone-800/50 rounded-xl border border-stone-100 dark:border-stone-800 transition-all";

// Category Configuration - Refined structure
export const CATEGORY_CONFIG: Record<Category, { id: string, icon: any, color: string, bg: string, desc: string }> = {
  [Category.PHOTO_STUDIO]: {
    id: "PHOTO_STUDIO",
    icon: Camera,
    color: "text-stone-900 dark:text-stone-300",
    bg: "bg-stone-100 dark:bg-stone-800",
    desc: "Natural light, cyklorama, backdrops, svetlá, tiché prostredie pre produkciu"
  },
  [Category.EVENT_SPACE]: {
    id: "EVENT_SPACE",
    icon: PartyPopper,
    color: "text-stone-900 dark:text-stone-300",
    bg: "bg-stone-100 dark:bg-stone-800",
    desc: "Oslavy, launch eventy, networking, svadby, firemné akcie"
  },
  [Category.MEETING_ROOM]: {
    id: "MEETING_ROOM",
    icon: Presentation,
    color: "text-stone-900 dark:text-stone-300",
    bg: "bg-stone-100 dark:bg-stone-800",
    desc: "Konferenčky, školenia, workshopy, AV technika, wifi"
  },
  [Category.WORKSHOP]: {
    id: "WORKSHOP",
    icon: Hammer,
    color: "text-stone-900 dark:text-stone-300",
    bg: "bg-stone-100 dark:bg-stone-800",
    desc: "Ateliéry, dielne, tvorba, skúšky, makerspace vibe"
  },
  [Category.OUTDOOR]: {
    id: "OUTDOOR",
    icon: Trees,
    color: "text-stone-900 dark:text-stone-300",
    bg: "bg-stone-100 dark:bg-stone-800",
    desc: "Záhrady, dvory, terasy, exteriéry pre natáčanie a eventy"
  },
  [Category.UNIQUE]: {
    id: "UNIQUE",
    icon: Sparkles,
    color: "text-teal-500",
    bg: "bg-stone-900",
    desc: "Lofty, industriál, historické priestory, unikátne lokácie"
  }
};

export const AMENITIES_OPTIONS = [
  'Wifi', 'Parkovanie', 'Klimatizácia', 'Bezbariérový prístup',
  'Kuchynka', 'Sprcha', 'Audio technika',
  'Video technika', 'Flipchart', 'Projektor/TV', 'Denné svetlo',
  'Zatemnenie', 'Šatňa', 'Make-up kútik',
  'Kávovar', 'Pitný režim (voda/čaj)', 'Chladnička', 'Terasa / Balkón',
  'Záhrada / Exteriér', 'Tlačiareň / Scanner', 'Biela tabuľa', 'Hudobné nástroje',
  'Foto pozadia', 'Štúdiové svetlá', 'Rekvizity', 'Zvuková izolácia',
  'Recepcia / Vrátnica', 'Bezpečnostný systém / Alarm', 'Úložný priestor',
  'Lounge / Oddychová zóna', 'Fajčiarsky kútik',
  'Zázemie pre účinkujúcich', 'Pódium', 'Profesionálne svetlá a zvuk',
  'Mikrofóny', 'Obrazovky / LED steny', 'Plátna rôznych rozmerov',
  'Haser (dymostroj)', 'Ubytovanie', 'Reštaurácia', ...ALL_CATERING_AMENITIES,
  'Výzdoba', 'DJ', 'Svadobná výzdoba',
  'Gril', 'Biliard', 'Pétanque', 'Šípky', 'Stolný futbal',
  'Wellness', 'Fitness', 'Tenisové kurty', 'Bazén', 'Ihrisko', 'Tanečný parket', 'Bowling', 'Detský kútik', 'Bar',
  'Livestream', 'Tlmočenie', 'Video spracovanie eventu'
];

export const EVENT_TYPE_OPTIONS = [
  // Kultúrne a spoločenské
  'Divadlo', 'Koncert', 'Talk show', 'Ples',
  // Firemné eventy
  'Meeting / Porada', 'Školenie', 'Prednáška', 'Prezentácia',
  'Seminár', 'Kongres', 'Workshop', 'Konferencia',
  'Výstava', 'Firemný večierok', 'Tímbilding',
  // Súkromné podujatia
  'Svadba', 'Oslava narodenín', 'Krstiny', 'Prijímanie', 'Birmovka', 'Promócie', 'Stužková',
  'Párty', 'Posedenie / Stretávka', 'Kar', 'Rozlúčka so slobodou',
  'Detská oslava', 'Babyshower', 'Vianočný večierok',
  // Produkcia
  'Fotografovanie', 'Natáčanie videa', 'Casting', 'Kurz / Joga',
];
/**
 * Interné SEO odkazy na hub stránky (mestá s dostatočným inventárom a intent
 * landing pages z IntentLandingPage). Zobrazujú sa vo footeri a na homepage,
 * aby hub stránky neboli pre crawlery siroty — sú v sitemape, ale bez
 * interných odkazov nedostávajú žiadnu váhu.
 */
export interface HubLink {
  label: string;
  to: string;
}

export const CITY_HUB_LINKS: HubLink[] = [
  { label: 'Bratislava', to: '/market/mesto/bratislava/' },
  { label: 'Nitra', to: '/market/mesto/nitra/' },
  { label: 'Košice', to: '/market/mesto/kosice/' },
  { label: 'Žilina', to: '/market/mesto/zilina/' },
  { label: 'Trnava', to: '/market/mesto/trnava/' },
  { label: 'Prešov', to: '/market/mesto/presov/' },
  { label: 'Trenčín', to: '/market/mesto/trencin/' },
  { label: 'Banská Bystrica', to: '/market/mesto/banska-bystrica/' },
];

export const INTENT_HUB_LINKS: HubLink[] = [
  { label: 'Priestory na oslavu (Bratislava)', to: '/oslava/priestory-na-oslavu-bratislava/' },
  { label: 'Narodeninová oslava (Bratislava)', to: '/oslava/narodeninova-oslava-bratislava/' },
  { label: 'Súkromný salónik (Bratislava)', to: '/oslava/sukromny-salonik-bratislava/' },
  { label: 'Školiaca miestnosť (Bratislava)', to: '/oslava/skoliaca-miestnost-bratislava/' },
  { label: 'Firemný večierok (Bratislava)', to: '/oslava/firemny-vecierok-bratislava/' },
  { label: 'Svadobné priestory (Bratislava)', to: '/oslava/svadobne-priestory-bratislava/' },
  { label: 'Teambuilding (Bratislava)', to: '/oslava/teambuilding-priestory-bratislava/' },
  { label: 'Ateliéry na fotenie (Bratislava)', to: '/oslava/priestory-na-fotenie-bratislava/' },
  { label: 'Priestory na oslavu (Košice)', to: '/oslava/priestory-na-oslavu-kosice/' },
  { label: 'Narodeninová oslava (Košice)', to: '/oslava/narodeninova-oslava-kosice/' },
  { label: 'Svadobné priestory (Košice)', to: '/oslava/svadobne-priestory-kosice/' },
  { label: 'Konferenčné priestory (Košice)', to: '/oslava/konferencne-priestory-kosice/' },
  { label: 'Priestory na oslavu (Žilina)', to: '/oslava/priestory-na-oslavu-zilina/' }
];
